/**
 * The rulebook: what the law asks of a website's cookies and trackers, per place.
 *
 * A place is a country (ISO 3166-1, "FR") or a subdivision ("US-CA", "CA-QC"). A visitor resolves
 * to the most specific place that has a rule, else their country, else the fallback. Rules can be
 * dated: a place may carry several entries, and the latest one whose `from` date has passed wins,
 * so a law with a known start date ships ahead of time and switches on by itself.
 *
 * Where the law depends on what a site does and we cannot tell from here, the rule takes the
 * stricter reading and says so in `why`.
 *
 * Dependency-free: used by the edge, the banner build, the dashboard and the scanner.
 */

import { covers, type Coverage, type SiteFacts } from './coverage';
import { ANALYTICS_NEEDS, exemptionHolds, type AnalyticsCheck, type AnalyticsNeed } from './analytics';

/** What a visitor in a place sees. */
export type Treatment = 'ask' | 'optout' | 'inform' | 'notice' | 'none';

/**
 * Every treatment, strictest first. ask: nothing optional until a yes. optout: tracking runs, a
 * privacy choices link to opt out. inform: tracking runs, a small notice says so and offers to turn
 * it off (Mexico, Canada). notice: analytics and preferences run with a notice and a way to object,
 * advertising waits for a yes. none: no banner.
 */
export const TREATMENTS: readonly Treatment[] = ['ask', 'optout', 'inform', 'notice', 'none'];

export const TREATMENT_LABELS: Record<Treatment, string> = {
  ask: 'Asks first',
  optout: 'Opt-out link',
  inform: 'Notice, can turn off',
  notice: 'Small notice',
  none: 'No banner',
};

export interface RuleSource {
  title: string;
  url: string;
}

export interface PlaceRule {
  /** "FR", "US-CA", "CA-QC". */
  place: string;
  name: string;
  treatment: Treatment;
  /** Honour the browser's Global Privacy Control signal as an opt-out. */
  gpc: boolean;
  /** The site must show a visible "Your privacy choices" link (opt-out regimes). */
  link?: boolean;
  /** The site must visibly confirm when a GPC signal was honoured (California since 1 Jan 2026). */
  gpcConfirm?: boolean;
  /** The banner may not be a bar along the bottom of the page (Nigeria). */
  noBar?: boolean;
  /** First day this entry applies (YYYY-MM-DD). Absent: always. */
  from?: string;
  /** Who the law covers, where it depends on the business (US state laws). Absent: every site. */
  appliesTo?: Coverage;
  /** What visitors see instead when the site's analytics qualify for the law's analytics exemption. */
  analyticsExempt?: Treatment;
  /** Conditions this place adds to its analytics exemption (France: short cookies and shortened IPs). */
  analyticsNeeds?: AnalyticsNeed[];
  /** One or two plain sentences: what the law asks and any judgement we made. */
  why: string;
  sources: RuleSource[];
}

export interface Rulebook {
  /** "2026.09.1": year, month, release in month. */
  version: string;
  /** When this release was approved (ISO). */
  published: string;
  /** What changed in this release, in plain words. */
  notes: string;
  fallback: PlaceRule;
  places: PlaceRule[];
}

/** How a site wants the law applied. */
export type Stance = 'law' | 'strict';

/**
 * Hosts the banner never blocks, whatever a rule, a list update or an owner says: payments,
 * checkout, fraud prevention, bot protection and sign-in. Blocking any of them can stop a sale.
 */
export const PROTECTED_HOSTS: readonly string[] = [
  // payments and checkout
  'js.stripe.com', 'm.stripe.com', 'm.stripe.network', 'checkout.stripe.com', 'hooks.stripe.com', 'api.stripe.com',
  'paypal.com', 'paypalobjects.com', 'venmo.com',
  'braintreegateway.com', 'braintree-api.com', 'braintreepayments.com',
  'adyen.com', 'adyenpayments.com',
  'klarna.com', 'klarnaservices.com', 'klarnacdn.net', 'afterpay.com', 'clearpay.co.uk', 'affirm.com', 'zip.co', 'sezzle.com',
  'squareup.com', 'squarecdn.com', 'square.site',
  'shop.app', 'shopifycdn.com', 'cdn.shopify.com', 'checkout.shopify.com', 'shopify.com/checkouts',
  'pay.google.com', 'apple-pay-gateway.apple.com', 'applepay.cdn-apple.com',
  'checkout.com', 'checkoutshopper-live.adyen.com', 'mollie.com', 'razorpay.com', 'mercadopago.com', 'paddle.com', 'lemonsqueezy.com', 'gumroad.com',
  'authorize.net', 'worldpay.com', 'globalpay.com', 'bolt.com', 'fastlane.paypal.com', 'link.com',
  'woocommerce.com/wc-api', 'wc-ajax', 'stripecdn.com',
  // donation checkouts
  'givebutter.com', 'givebuttercdn.com', 'donorbox.org', 'classy.org', 'zeffy.com', 'every.org', 'fundraiseup.com',
  // fraud and risk
  'signifyd.com', 'riskified.com', 'forter.com', 'sift.com', 'sardine.ai', 'kount.net', 'seon.io', 'nofraud.com',
  // bot protection and captchas
  'recaptcha.net', 'google.com/recaptcha', 'gstatic.com/recaptcha', 'hcaptcha.com', 'challenges.cloudflare.com', 'arkoselabs.com', 'funcaptcha.com',
  // sign-in
  'accounts.google.com', 'appleid.cdn-apple.com', 'appleid.apple.com', 'login.microsoftonline.com', 'auth0.com', 'clerk.com', 'clerk.accounts.dev', 'stytch.com', 'okta.com',
];

/** True if a script or frame URL belongs to a protected provider. Matching is by host suffix or path fragment. */
export function isProtected(url: string | null | undefined): boolean {
  if (!url) return false;
  const u = url.toLowerCase();
  let host = '';
  try {
    host = new URL(u, 'https://x.invalid').hostname;
  } catch {
    /* relative or odd: host stays empty and path fragments still match */
  }
  return PROTECTED_HOSTS.some((p) => {
    if (p.includes('/')) return u.includes(p);
    return host === p || host.endsWith(`.${p}`) || (!p.includes('.') && u.includes(p));
  });
}

/** Most specific rule for a visitor, as of `today` (YYYY-MM-DD). */
export function resolvePlace(book: Rulebook, country: string, subdivision: string | null | undefined, today: string): PlaceRule {
  const c = (country || '').toUpperCase();
  const s = (subdivision || '').toUpperCase();
  const pick = (place: string): PlaceRule | null => {
    let best: PlaceRule | null = null;
    for (const r of book.places) {
      if (r.place !== place) continue;
      if (r.from && r.from > today) continue;
      if (!best || (r.from ?? '') > (best.from ?? '')) best = r;
    }
    return best;
  };
  return (s && pick(`${c}-${s}`)) || pick(c) || book.fallback;
}

export interface SitePolicy {
  stance?: Stance;
  /** Ask first in California, as a shield against pixel lawsuits. Off by default. */
  caShield?: boolean;
  /** The owner's own choices for particular places, keyed by place ("GB", "US-CA"). */
  placeOverrides?: Record<string, Treatment>;
  /** Facts some laws turn on: size for US state laws, analytics for analytics exemptions. */
  facts?: SiteFacts;
  /** What our site check found about the site's analytics. */
  analytics?: AnalyticsCheck;
}

export interface Applied {
  rule: PlaceRule;
  treatment: Treatment;
  /**
   * Where the treatment came from, for the dashboard and the record. 'not covered': the law doesn't
   * cover this business; 'exempt': its analytics qualify for the law's exemption.
   */
  reason: 'law' | 'strict' | 'shield' | 'override' | 'not covered' | 'exempt';
  /**
   * Categories that run before the visitor chooses because the law exempts them here (the site's own
   * visit statistics); the banner still asks about everything else, and Reject turns them off too.
   */
  preRun?: 'analytics'[];
}

/** The treatment a site gives a visitor: the law, then the site's stance, shield and overrides. */
export function applyPolicy(policy: SitePolicy, rule: PlaceRule): Applied {
  const country = rule.place.split('-')[0]!;
  const override = policy.placeOverrides?.[rule.place] ?? policy.placeOverrides?.[country];
  if (override) return { rule, treatment: override, reason: 'override' };
  if (policy.caShield && rule.place === 'US-CA') return { rule, treatment: 'ask', reason: 'shield' };
  // Asking first everywhere means no exemptions either, even where the law itself asks first.
  if (policy.stance === 'strict') return { rule, treatment: 'ask', reason: rule.treatment === 'ask' ? 'law' : 'strict' };
  if (rule.appliesTo && covers(rule.appliesTo, policy.facts) === false) return { rule, treatment: 'none', reason: 'not covered' };
  // Analytics the law exempts here, as our site check found them: they run; the rest is asked about
  // as the law says. With nothing else to ask about, the exemption's own treatment applies.
  if (rule.analyticsExempt && exemptionHolds(rule.analyticsNeeds, policy.analytics, policy.facts?.ipMasked)) {
    return policy.analytics!.others || rule.treatment !== 'ask'
      ? { rule, treatment: rule.treatment, reason: 'exempt', preRun: ['analytics'] }
      : { rule, treatment: rule.analyticsExempt, reason: 'exempt' };
  }
  return { rule, treatment: rule.treatment, reason: 'law' };
}

/** Upcoming dated entries (from after today), soonest first. */
export function upcoming(book: Rulebook, today: string): PlaceRule[] {
  return book.places.filter((r) => r.from && r.from > today).sort((a, b) => (a.from! < b.from! ? -1 : 1));
}

/** Per-place differences between two rulebooks as of a date: what a visitor there would see differently. */
export interface RuleChange {
  place: string;
  name: string;
  before: PlaceRule | null;
  after: PlaceRule | null;
  fields: string[];
}

export function diffRulebooks(a: Rulebook | null, b: Rulebook): RuleChange[] {
  const key = (r: PlaceRule) => `${r.place}@${r.from ?? ''}`;
  const left = new Map((a?.places ?? []).map((r) => [key(r), r]));
  const right = new Map(b.places.map((r) => [key(r), r]));
  const out: RuleChange[] = [];
  const compare = (x: PlaceRule | null, y: PlaceRule | null): string[] => {
    if (!x || !y) return ['added or removed'];
    const fields = (['treatment', 'gpc', 'link', 'gpcConfirm', 'noBar', 'analyticsExempt', 'why'] as const).filter((f) => (x[f] ?? null) !== (y[f] ?? null)) as string[];
    if (JSON.stringify(x.analyticsNeeds ?? null) !== JSON.stringify(y.analyticsNeeds ?? null)) fields.push('analyticsNeeds');
    if (JSON.stringify(x.appliesTo ?? null) !== JSON.stringify(y.appliesTo ?? null)) fields.push('appliesTo');
    return fields;
  };
  for (const [k, r] of right) {
    const before = left.get(k) ?? null;
    const fields = compare(before, r);
    if (fields.length) out.push({ place: r.place, name: r.name, before, after: r, fields });
  }
  for (const [k, r] of left) if (!right.has(k)) out.push({ place: r.place, name: r.name, before: r, after: null, fields: ['added or removed'] });
  if (a && JSON.stringify(a.fallback) !== JSON.stringify(b.fallback)) out.push({ place: '*', name: 'Everywhere else', before: a.fallback, after: b.fallback, fields: compare(a.fallback, b.fallback) });
  return out.sort((x, y) => x.place.localeCompare(y.place));
}

/** Basic shape checks before a rulebook is saved or served. Returns problems in plain words. */
export function validateRulebook(book: unknown): string[] {
  const problems: string[] = [];
  const b = book as Partial<Rulebook> | null;
  if (!b || typeof b !== 'object') return ['Not a rulebook.'];
  if (!b.version || !/^\d{4}\.\d{2}\.\d+$/.test(b.version)) problems.push('Version must look like 2026.09.1.');
  const treatments = TREATMENTS;
  const check = (r: Partial<PlaceRule>, where: string) => {
    if (!r.place || !/^([A-Z]{2}(-[A-Z0-9]{1,3})?|\*)$/.test(r.place)) problems.push(`${where}: place must be like FR or US-CA.`);
    if (!r.name) problems.push(`${where}: needs a name.`);
    if (!r.treatment || !treatments.includes(r.treatment)) problems.push(`${where}: treatment must be one of ${TREATMENTS.join(', ')}.`);
    if (typeof r.gpc !== 'boolean') problems.push(`${where}: gpc must be true or false.`);
    if (r.from && !/^\d{4}-\d{2}-\d{2}$/.test(r.from)) problems.push(`${where}: from must be YYYY-MM-DD.`);
    if (r.analyticsExempt !== undefined && !treatments.includes(r.analyticsExempt)) problems.push(`${where}: analyticsExempt must be a treatment.`);
    if (r.analyticsNeeds !== undefined && (!Array.isArray(r.analyticsNeeds) || r.analyticsNeeds.some((n) => !ANALYTICS_NEEDS.includes(n)))) problems.push(`${where}: analyticsNeeds must list ${ANALYTICS_NEEDS.join(' or ')}.`);
    if (r.analyticsNeeds !== undefined && r.analyticsExempt === undefined) problems.push(`${where}: analyticsNeeds without analyticsExempt.`);
    if (r.appliesTo !== undefined) {
      const a = r.appliesTo as unknown as Record<string, unknown>;
      if (!a || typeof a !== 'object') problems.push(`${where}: appliesTo must be an object.`);
      else {
        for (const k of Object.keys(a)) if (!['any', 'smallBusinessExempt', 'nonprofitExempt'].includes(k)) problems.push(`${where}: appliesTo.${k} is not known.`);
        for (const k of ['smallBusinessExempt', 'nonprofitExempt']) if (a[k] !== undefined && typeof a[k] !== 'boolean') problems.push(`${where}: appliesTo.${k} must be true or false.`);
        if (a.any !== undefined && !Array.isArray(a.any)) problems.push(`${where}: appliesTo.any must be a list of thresholds.`);
        for (const t of (Array.isArray(a.any) ? a.any : []) as Record<string, unknown>[])
          for (const [k, v] of Object.entries(t ?? {})) {
            if (!['revenueOverUSD', 'peopleOver', 'salesShareOver'].includes(k)) problems.push(`${where}: threshold ${k} is not known.`);
            else if (typeof v !== 'number' || v < 0 || (k === 'salesShareOver' && v > 1)) problems.push(`${where}: threshold ${k} must be a number${k === 'salesShareOver' ? ' from 0 to 1' : ''}.`);
          }
      }
    }
    if (!r.why) problems.push(`${where}: explain why in one or two sentences.`);
  };
  if (!b.fallback) problems.push('Needs a fallback rule for everywhere else.');
  else check(b.fallback, 'Everywhere else');
  for (const [i, r] of (b.places ?? []).entries()) check(r, r.place ?? `Rule ${i + 1}`);
  const seen = new Set<string>();
  for (const r of b.places ?? []) {
    const k = `${r.place}@${r.from ?? ''}`;
    if (seen.has(k)) problems.push(`${r.place}: two entries with the same start date.`);
    seen.add(k);
  }
  return problems;
}
